import type { ReactNode } from 'react'
import {
  BadgeCheck,
  CircleEllipsis,
  Lightbulb,
  MessageCircle,
  Package,
  ShieldAlert,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { badgeColor, cn } from '@/lib/utils'
import type { EnumOption } from '@/types/api'

/**
 * The server names an icon per report type (`SubmissionType::icon()`), in
 * lucide's kebab-case. Mapped here rather than imported by name at runtime,
 * so the bundle carries seven icons and not the whole set; an icon the server
 * adds later falls back to the generic one until it is listed.
 */
const TYPE_ICONS: Record<string, LucideIcon> = {
  wrench: Wrench,
  'shield-alert': ShieldAlert,
  'badge-check': BadgeCheck,
  package: Package,
  lightbulb: Lightbulb,
  'message-circle': MessageCircle,
  'circle-ellipsis': CircleEllipsis,
}

/**
 * One choice out of a few, as large tiles.
 *
 * Real radio buttons underneath, so the arrow keys move between them, a
 * screen reader announces "1 of 7, selected", and the form's own reset and
 * validation still see a value. The tile is the label, so the whole of it is
 * the target.
 */
export function TypeTiles({
  name,
  options,
  value,
  onChange,
  legend,
}: {
  name: string
  options: EnumOption[]
  value: string
  onChange: (value: string) => void
  legend: ReactNode
}) {
  return (
    <fieldset>
      <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium">{legend}</legend>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {options.map((option) => {
          const Icon = TYPE_ICONS[option.icon ?? ''] ?? CircleEllipsis
          const checked = value === option.value

          return (
            <label
              key={option.value}
              className={cn(
                'relative flex cursor-pointer items-center gap-2.5 rounded-xl border p-3 text-sm font-medium transition-colors',
                'has-focus-visible:ring-ring/50 has-focus-visible:ring-3',
                checked
                  ? 'border-primary bg-primary/10 ring-primary/30 ring-1'
                  : 'hover:bg-muted/60',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-lg',
                  badgeColor(option.color),
                )}
              >
                <Icon className="size-5" aria-hidden />
              </span>
              <span className="min-w-0 leading-tight">{option.label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

/**
 * A short row of choices — the priority — as one segmented control.
 * Same radio buttons underneath as the tiles, at the height of a button.
 */
export function Segmented({
  name,
  options,
  value,
  onChange,
  legend,
}: {
  name: string
  options: EnumOption[]
  value: string
  onChange: (value: string) => void
  legend: ReactNode
}) {
  return (
    <fieldset>
      <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium">{legend}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const checked = value === option.value

          return (
            <label
              key={option.value}
              className={cn(
                'min-h-control inline-flex cursor-pointer items-center gap-1.5 rounded-xl border px-3 text-sm transition-colors',
                'has-focus-visible:ring-ring/50 has-focus-visible:ring-3',
                checked ? 'border-primary bg-primary/10 font-semibold' : 'hover:bg-muted/60',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={cn('size-2 rounded-full', badgeColor(option.color))}
              />
              {option.label}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
